import { useQuery } from '@tanstack/react-query'

import type {
  EmailConfigRes,
  EmailLogsRes,
  EmailRobotRunRes,
  EmailRobotStatusRes,
  EmailStatsRes,
  EmailSubscriptionsRes,
  PatchEmailConfigDto,
  UpdateEmailRobotConfigDto,
} from '@brickup/shared/dto'
import { EmailStatus } from '@brickup/shared/enums'
import { cronJobApi } from './cron-job'
import type { GlanceOptions } from './glance'
import { apiClient } from '@/api/client'
import { EMAIL_STATS_KEY } from '@/api/query-keys'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'
import { useOffsetInfiniteList } from '@/hooks/use-offset-infinite-list'

const EMAIL_PAGE_SIZE = 20

type EmailLogsParams = {
  skip?: number
  take?: number
  status?: EmailStatus
  templateKey?: string
  search?: string
}

export async function getEmailLogs(
  params?: EmailLogsParams,
): Promise<EmailLogsRes> {
  const { data } = await apiClient.get<EmailLogsRes>(
    '/admin/email-management/logs',
    { params },
  )
  return data
}

export async function getEmailStats(): Promise<EmailStatsRes> {
  const { data } = await apiClient.get<EmailStatsRes>(
    '/admin/email-management/stats',
  )
  return data
}

/** What the mailer has sent and failed to send, overall and in the last day. */
export function useEmailStats(options?: GlanceOptions) {
  return useQuery({
    queryKey: EMAIL_STATS_KEY,
    queryFn: getEmailStats,
    ...options,
  })
}

type EmailLogsFilters = {
  status?: EmailStatus
  search?: string
}

export function useEmailLogs(filters: EmailLogsFilters = {}) {
  return useOffsetInfiniteList({
    queryKey: ['email-logs', filters],
    fetchPage: page => getEmailLogs({ ...page, ...filters }),
    pageSize: EMAIL_PAGE_SIZE,
  })
}

type SubscriptionsParams = {
  skip?: number
  take?: number
  unsubscribed?: boolean
  search?: string
}

export async function getSubscriptions(
  params?: SubscriptionsParams,
): Promise<EmailSubscriptionsRes> {
  const { data } = await apiClient.get<EmailSubscriptionsRes>(
    '/admin/email-management/subscriptions',
    { params },
  )
  return data
}

type SubscriptionFilters = {
  unsubscribed?: boolean
  search?: string
}

/**
 * The subscribed/unsubscribed tallies describe the whole table and ride along
 * on every page, so they cost no request of their own.
 */
export function useSubscriptions(filters: SubscriptionFilters = {}) {
  const list = useOffsetInfiniteList({
    queryKey: ['email-subscriptions', filters],
    fetchPage: page => getSubscriptions({ ...page, ...filters }),
    pageSize: EMAIL_PAGE_SIZE,
  })

  return {
    ...list,
    subscribedCount: list.firstPage?.subscribedCount ?? 0,
    unsubscribedCount: list.firstPage?.unsubscribedCount ?? 0,
  }
}

export function useResubscribeUser() {
  return useMutationWithInvalidation(resubscribeUser, [['email-subscriptions']])
}

export async function resubscribeUser(
  userId: number,
): Promise<{ success: boolean; message: string }> {
  const { data } = await apiClient.post<{ success: boolean; message: string }>(
    `/admin/email-management/subscriptions/${userId}/resubscribe`,
  )
  return data
}

export function useUnsubscribeUser() {
  return useMutationWithInvalidation(unsubscribeUser, [['email-subscriptions']])
}

export async function unsubscribeUser(
  userId: number,
): Promise<{ success: boolean; message: string }> {
  const { data } = await apiClient.post<{ success: boolean; message: string }>(
    `/admin/email-management/subscriptions/${userId}/unsubscribe`,
  )
  return data
}

export async function getEmailConfig(): Promise<EmailConfigRes> {
  const { data } = await apiClient.get<EmailConfigRes>(
    '/admin/email-management/config',
  )
  return data
}

export async function updateEmailConfig(
  config: PatchEmailConfigDto,
): Promise<EmailConfigRes> {
  const { data } = await apiClient.patch<EmailConfigRes>(
    '/admin/email-management/config',
    config,
  )
  return data
}

const EMAIL_CONFIG_KEY = ['email-config']

const robotJob = cronJobApi<
  EmailRobotStatusRes,
  EmailRobotRunRes,
  UpdateEmailRobotConfigDto
>('email-robot', {
  status: '/admin/email-management/robot/status',
  config: '/admin/email-management/robot/config',
  trigger: '/admin/email-management/robot/run',
})

/** The email robot: its switch, its interval, its last and next run and what
 *  the mailer has recorded. */
export const useEmailRobotStatus = robotJob.useStatus
export const useEmailRobotMutations = robotJob.useMutations

/**
 * The one email-config record, and the one way to change it.
 *
 * Two settings sections read it — the mail block and the HubSpot pipeline
 * toggle — and each held its own query, its own mutation and its own snackbar
 * pair, so the same record was fetched twice and the two disagreed about which
 * caches a write drops.
 */
export function useEmailConfig() {
  const query = useQuery({
    queryKey: EMAIL_CONFIG_KEY,
    queryFn: getEmailConfig,
    // The two sections that read it are separate routes, so the record would
    // otherwise be fetched again on every step between them.
    staleTime: 5 * 60_000,
  })

  const update = useMutationWithInvalidation(updateEmailConfig, [
    EMAIL_CONFIG_KEY,
  ])

  return { config: query.data, isLoading: query.isLoading, update }
}
